import { gatewayV1Abi } from "@/abi";
import { ALLOWED_LOCK_DURATION_VALUES, getGatewayContractAddress } from "@/config";
import type { TransactionHash } from "@/types";
import { epoch, estimateContractTransactionGas, writeContractTransaction } from "@/utils";
import { type TransactionRequest, type WalletClient, encodeFunctionData } from "viem";

export function getNewUnwindingEpochs(unwindingEpochs: number, unwindingTimestampMs: number): bigint {
    const unwindingTimestamp = BigInt(Math.floor(unwindingTimestampMs / 1000));
    const initialEpoch = epoch(unwindingTimestamp);
    const currentEpoch = epoch(BigInt(Math.floor(Date.now() / 1000)));
    const epochsPassed = currentEpoch - initialEpoch;

    if (epochsPassed > unwindingEpochs) {
        throw new Error("User has already unwound");
    }

    const epochsRemaining = BigInt(unwindingEpochs) - epochsPassed;

    for (const value of ALLOWED_LOCK_DURATION_VALUES) {
        if (BigInt(value) > epochsRemaining) {
            return BigInt(value);
        }
    }

    throw new Error("Invalid path. Unknown error occured");
}

/**
 * Prepares a cancel unwinding transaction
 */
export const prepareCancelUnwindingTransaction = async (
    newUnwindingEpochs: number,
    unwindingTimestampMs: number,
    walletClient: WalletClient,
): Promise<TransactionRequest> => {
    if (!walletClient.account) {
        throw new Error("Wallet account is not available");
    }

    if (!walletClient.chain?.id) {
        throw new Error("Chain ID is not available");
    }

    const contractAddress = getGatewayContractAddress(walletClient.chain.id);
    const unwindingTimestamp = Math.floor(unwindingTimestampMs / 1000);

    return {
        to: contractAddress,
        data: encodeFunctionData({
            abi: gatewayV1Abi,
            functionName: "cancelUnwinding",
            args: [unwindingTimestamp, newUnwindingEpochs],
        }),
    };
};

/**
 * Estimates gas for a cancel unwinding transaction
 */
export const estimateCancelUnwindingTransactionGas = async (
    newUnwindingEpochs: number,
    unwindingTimestamp: number,
    walletClient: WalletClient,
): Promise<bigint> => {
    if (!walletClient.account) {
        throw new Error("Wallet account is not available");
    }

    const txRequest = await prepareCancelUnwindingTransaction(newUnwindingEpochs, unwindingTimestamp, walletClient);
    return estimateContractTransactionGas(walletClient, txRequest);
};

/**
 * Executes a cancel unwinding transaction
 */
export const gatewayCancelUnwindingTransaction = async (
    newUnwindingEpochs: number,
    unwindingTimestamp: number,
    walletClient: WalletClient,
): Promise<TransactionHash> => {
    const txRequest = await prepareCancelUnwindingTransaction(newUnwindingEpochs, unwindingTimestamp, walletClient);
    const hash = await writeContractTransaction(walletClient, txRequest);
    return hash as TransactionHash;
};
